import { useEffect, useState } from "react";
import type { OverlayScrollState, OverlayViewState } from "@cuekards/core/ipc";
import type { RecordingStatus } from "@cuekards/core/recording";
import { IDLE_STATUS } from "@cuekards/core/recording";
import { DEFAULT_SCROLL_MODE, scrollbarLive } from "@cuekards/core/scroll-mode";
import {
  buildScriptModel,
  EMPTY_MODEL,
  type ScriptModel,
} from "@cuekards/core/script-model";
import { ANCHOR_FRACTION, Prompter } from "./OverlayApp.js";
import { PrompterCorner, StatusBar } from "./prompter-chrome.js";
import { useFollowerState } from "./use-follower-state.js";

/* What the prompt is drawn with for the frame before the host has answered.
 * `OverlayApp` carries the same pair for the same reason, and the anchor is read
 * off it rather than written again — it is `ANCHOR_FRACTION`, a third, which is
 * where the reading line rode before it could be moved at all. */
const DEFAULT_VIEW: OverlayViewState = {
  fontSize: 30,
  anchor: ANCHOR_FRACTION,
  side: 0.5,
  mirror: false,
  lineSpacing: 1,
};
const DEFAULT_SCROLL: OverlayScrollState = {
  mode: DEFAULT_SCROLL_MODE,
  offScript: false,
  wpm: 0,
  speechUnavailable: false,
  playing: false,
};

/**
 * The script you read from, as a pane of the desktop's one window (one-window
 * ticket 05).
 *
 * What the overlay window used to be, less the parts that outgrew it. The
 * spoken-stop prompt moved up to the shell — it is a question with a five-second
 * deadline and has to be visible from the settings pane too — and the transport
 * was never in here at all.
 *
 * **The follow strip was here and is gone.** It was a bordered box under the
 * script holding two escape hatches, and it cost more of the window than either
 * of them was worth. Rehearse is a link in the transport's left track now;
 * back-to-top is an icon over the script it moves, in `PrompterCorner`. What
 * the strip was right about — that both belong beside the script rather than
 * in the settings, because they are about the document and not about how
 * cuekards is set up — is unchanged; they are simply beside it in less space.
 *
 * **The script scrolls here and only here** (scroll-and-bar ticket 03). The
 * scrollbar is live exactly when nothing else is driving the anchor — see
 * `scrollbarLive` — which is what keeps the old guarantee intact rather than
 * trading it away: a document that moves under a line being positioned by voice is
 * the line in the wrong place, so while a voice is positioning it, nothing else
 * may move it.
 *
 * This is also the one thing in the window that needs four pieces of host state at
 * once. Three of them were already here for the prompt itself; the fourth is the
 * follower, which is why this pane subscribes to it as well as the transport does.
 *
 * `Prompter` used to have to be a direct child of `.overlay`, because it anchored
 * the reading line on `parentElement.clientHeight`. It renders and measures its own
 * scroll container now, so that invariant is gone.
 */
export function PrompterPane(): JSX.Element {
  const [model, setModel] = useState<ScriptModel>(EMPTY_MODEL);
  const [position, setPosition] = useState(-1);
  const [view, setView] = useState<OverlayViewState>(DEFAULT_VIEW);
  const [scroll, setScroll] = useState<OverlayScrollState>(DEFAULT_SCROLL);
  const [recording, setRecording] = useState<RecordingStatus>(IDLE_STATUS);
  const follower = useFollowerState();

  useEffect(() => {
    let cancelled = false;
    // Pull whatever the host already holds before this view mounted, then follow
    // live updates — so a broadcast that fired before mount is never missed.
    // Which matters more here than it did in a window of its own: this pane is
    // unmounted whenever the setup or settings pane is open, and everything it
    // draws can change while it is away.
    void window.cuekards.getScript().then((s) => {
      if (!cancelled) setModel(s ? buildScriptModel(s.text) : EMPTY_MODEL);
    });
    void window.cuekards.getPosition().then((p) => {
      if (!cancelled) setPosition(p);
    });
    void window.cuekards.getOverlayView().then((v) => {
      if (!cancelled) setView(v);
    });
    void window.cuekards.getScrollState().then((s) => {
      if (!cancelled) setScroll(s);
    });
    void window.cuekards.getRecordingStatus().then((s) => {
      if (!cancelled) setRecording(s);
    });
    const offScriptLoaded = window.cuekards.onScriptLoaded((p) =>
      setModel(buildScriptModel(p.text)),
    );
    const offPosition = window.cuekards.onPositionChanged((p) => setPosition(p));
    const offView = window.cuekards.onOverlayViewChanged((v) => setView(v));
    const offScroll = window.cuekards.onScrollStateChanged((s) => setScroll(s));
    const offRecording = window.cuekards.onRecordingStatusChanged(setRecording);
    return () => {
      cancelled = true;
      offScriptLoaded();
      offPosition();
      offView();
      offScroll();
      offRecording();
    };
  }, []);

  // Font size is applied as a CSS variable so the shortcuts (FR-27) resize the
  // prompt live without re-rendering its structure. Line spacing rides beside
  // it for the same reason (prompter-display ticket 04).
  const style = {
    "--overlay-font-size": `${view.fontSize}px`,
    "--overlay-line-spacing": String(view.lineSpacing),
  } as React.CSSProperties;

  // Four facts, one answer: is anything other than the reader's hands moving the
  // anchor right now? The rule itself is in core, where it can be tested against
  // every combination without a DOM.
  const scrollable = scrollbarLive({
    mode: scroll.mode,
    phase: recording.phase,
    rehearsing: follower.rehearsing,
    position,
    wordCount: model.wordCount,
  });

  return (
    <div className="prompter-pane">
      <div className="overlay in-pane" style={style}>
        {model.lines.length === 0 ? (
          <p className="overlay-placeholder">No script loaded</p>
        ) : (
          <Prompter
            model={model}
            position={position}
            fontSize={view.fontSize}
            anchor={view.anchor}
            mirror={view.mirror}
            lineSpacing={view.lineSpacing}
            scrollable={scrollable}
          />
        )}
        <StatusBar scroll={scroll} />
        <PrompterCorner status={recording} />
      </div>
    </div>
  );
}
